import { emptyParticipant } from "../utils/emptyObjects";
import { findParticipants } from "../utils/firestore";
import { FormData } from "../interfaces/IForms";
import { mapFormDataToObject, normalizeFirstName, normalizeName } from "../utils/utils";
import { Participant, ParticipantWithId } from "../interfaces/IParticipants";
import { useEffect, useState } from "react";
import { useParticipantLogging } from "./useParticipantLogging";
import { useParticipantOperations } from "./useParticipantOperations";
import { useParticipantValidation } from "./useParticipantValidation";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  participant?: ParticipantWithId;
}

/**
 * Główny hook do zarządzania zawodnikami
 * Działa jako orkiestrator - koordynuje walidację, operacje i logowanie
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko orkiestracja
 */
export const useParticipants = (userId: string) => {
  const [participants, setParticipants] = useState<ParticipantWithId[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Hooki pomocnicze
  const { validateDuplicate, validateParticipantData } = useParticipantValidation();
  const { logCreate, logUpdate, logDelete } = useParticipantLogging();
  const { performAdd, performUpdate, performDelete } = useParticipantOperations(userId);

  const fetchData = async () => {
    setIsLoading(true);
    if (userId) {
      try {
        const data = await findParticipants(userId as string);
        setParticipants(data);
      } catch (error) {
        console.error("Error fetching participants:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  /**
   * Mapuje dane z formularza na obiekt Participant
   */
  const mapFormDataToParticipant = (formData: FormData, userId: string): Participant => {
    const mappedData = mapFormDataToObject<Participant>(formData, emptyParticipant);

    return {
      ...mappedData,
      firstName: normalizeFirstName(mappedData.firstName),
      lastName: normalizeName(mappedData.lastName),
      userId: userId,
    };
  };

  // ===== ORKIESTROWANE METODY =====

  /**
   * Dodaje nowego zawodnika - orkiestracja walidacji, operacji i logowania
   */
  const handleParticipantAdd = async (participantData: Participant): Promise<OperationResult> => {
    try {
      // 1. Walidacja duplikatów
      if (validateDuplicate(participants, participantData)) {
        return {
          success: false,
          error: "Zawodnik o takich danych już istnieje (imię, nazwisko, data urodzenia).",
        };
      }

      // 2. Walidacja danych
      const validationErrors = validateParticipantData(participantData);
      if (validationErrors.length > 0) {
        return {
          success: false,
          error: validationErrors.join(", "),
        };
      }

      // 3. Operacja na Firestore
      const result = await performAdd(participantData);
      if (!result.success) {
        return result;
      }

      // 4. Logowanie sukcesu
      await logCreate(result.participant!, true, undefined, { userId });

      // 5. Aktualizacja stanu
      setParticipants((prev) => [...prev, result.participant!]);

      return { success: true, participant: result.participant };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      await logCreate(participantData, false, errorMessage, { userId });
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Dodaje zawodnika z formularza - orkiestracja mapowania i dodawania
   */
  const handleParticipantAddFromForm = async (formData: FormData): Promise<OperationResult> => {
    try {
      // 1. Mapowanie i normalizacja danych
      const participant = mapFormDataToParticipant(formData, userId);

      // 2. Delegacja do głównej metody
      return await handleParticipantAdd(participant);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Aktualizuje zawodnika - orkiestracja operacji i logowania
   */
  const handleParticipantUpdate = async (
    participantId: string,
    updatedData: Partial<ParticipantWithId>
  ): Promise<OperationResult> => {
    try {
      // 1. Znajdź stary stan
      const oldParticipant = participants.find((p) => p.id === participantId);
      if (!oldParticipant) {
        return { success: false, error: "Zawodnik nie został znaleziony" };
      }

      // 2. Normalizacja danych (jeśli zostały zmienione)
      const normalizedUpdatedData: Partial<ParticipantWithId> = {
        ...updatedData,
        ...(updatedData.firstName !== undefined && {
          firstName: normalizeFirstName(updatedData.firstName),
        }),
        ...(updatedData.lastName !== undefined && {
          lastName: normalizeName(updatedData.lastName),
        }),
      };

      // 3. Przygotuj nowy stan z znormalizowanymi danymi
      const newParticipant = { ...oldParticipant, ...normalizedUpdatedData };

      // 4. Walidacja duplikatów - sprawdzamy tylko jeśli zmieniono imię, nazwisko lub datę urodzenia
      const hasIdentityChanged =
        normalizedUpdatedData.firstName !== undefined ||
        normalizedUpdatedData.lastName !== undefined ||
        normalizedUpdatedData.birthDate !== undefined;

      if (hasIdentityChanged) {
        if (validateDuplicate(participants, newParticipant, participantId)) {
          return {
            success: false,
            error:
              "Nie można zapisać zmian.Zawodnik o takich danych już istnieje (imię, nazwisko, data urodzenia).",
          };
        }
      }

      // 5. Walidacja danych
      const validationErrors = validateParticipantData(newParticipant);
      if (validationErrors.length > 0) {
        return {
          success: false,
          error: validationErrors.join(", "),
        };
      }

      // 6. Operacja na Firestore (używamy pełnego obiektu z znormalizowanymi danymi)
      const result = await performUpdate(participantId, newParticipant);
      if (!result.success) {
        return result;
      }

      // 7. Aktualizuj stan lokalny
      setParticipants((prev) => prev.map((p) => (p.id === participantId ? newParticipant : p)));

      // 8. Logowanie sukcesu
      await logUpdate(oldParticipant, newParticipant, true, undefined, { userId });

      return { success: true, participant: newParticipant };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const oldParticipant = participants.find((p) => p.id === participantId);
      if (oldParticipant) {
        await logUpdate(
          oldParticipant,
          { ...oldParticipant, ...updatedData },
          false,
          errorMessage,
          { userId }
        );
      }
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Usuwa zawodnika (soft delete) - orkiestracja operacji i logowania
   */
  const handleParticipantDelete = async (participantId: string): Promise<OperationResult> => {
    try {
      // 1. Znajdź zawodnika
      const participantToDelete = participants.find((p) => p.id === participantId);
      if (!participantToDelete) {
        return { success: false, error: "Zawodnik nie został znaleziony" };
      }

      // 2. Operacja na Firestore
      const result = await performDelete(participantId);
      if (!result.success) {
        return result;
      }

      // 3. Aktualizuj stan lokalny – usuń z listy (lista zawiera tylko aktywnych)
      setParticipants((prev) => prev.filter((p) => p.id !== participantId));

      // 4. Logowanie sukcesu
      await logDelete(participantToDelete, true, undefined, { userId });

      return { success: true };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const participantToDelete = participants.find((p) => p.id === participantId);
      if (participantToDelete) {
        await logDelete(participantToDelete, false, errorMessage, { userId });
      }
      return { success: false, error: errorMessage };
    }
  };

  return {
    // Stan
    participants,
    isLoading,

    // Orkiestrowane metody
    handleParticipantAdd,
    handleParticipantAddFromForm,
    handleParticipantUpdate,
    handleParticipantDelete,
    refreshParticipants: fetchData,
  };
};
